import { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { FinancingConditionsSelection } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditFinancingStep/EditFinancingStep';
import {
  getRegistrationDateChangeState,
  getVehicleAmountChangeState,
  isAutomobileFormItemDirty,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodDirty.map';
import {
  EditAutoPaymentMethodFinancingFields,
  EditAutoPaymentMethodFormFields,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { EditAutoPaymentMethodEntryMode } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodEntryMode';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import { PaymentMethodModificationOptions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/maps/vehicleModificationPreview.map';
import { isDeepEqual } from '@/utils/isObjectDeepEqual';

interface UseEditAutoPaymentMethodConfirmationParams {
  entryMode: EditAutoPaymentMethodEntryMode;
  initialFormItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
}

export const useEditAutoPaymentMethodConfirmation = ({
  entryMode,
  initialFormItem,
  initialFinancing,
}: UseEditAutoPaymentMethodConfirmationParams) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EditAutoPaymentMethodFormFields>();
  const currentItem = useWatch({ control, name: 'item' }) as AutomobileFormItem;
  const currentFinancing = useWatch({ control, name: 'financing' }) as EditAutoPaymentMethodFinancingFields;

  const [lastConfirmedVehicleAmount, setLastConfirmedVehicleAmount] = useState(initialFormItem.price);
  const [lastConfirmedRegistrationDate, setLastConfirmedRegistrationDate] = useState<Date | string | null | undefined>(
    initialFormItem.registrationDate,
  );
  const [selectedPaymentMethodOptions, setSelectedPaymentMethodOptions] = useState<
    PaymentMethodModificationOptions | undefined
  >(undefined);

  const vehicleAmountChange = getVehicleAmountChangeState(
    initialFormItem.price,
    currentItem?.price,
    lastConfirmedVehicleAmount,
  );
  const registrationDateChange = getRegistrationDateChangeState(
    initialFormItem.registrationDate,
    currentItem?.registrationDate,
    lastConfirmedRegistrationDate,
  );

  const needsFinancingReselection =
    vehicleAmountChange.isChangedSinceConfirmation || registrationDateChange.isChangedSinceConfirmation;
  const requiresSimulatorFromVehicle =
    vehicleAmountChange.isChangedFromInitial || registrationDateChange.isChangedFromInitial;

  const isItemDirty = isAutomobileFormItemDirty(initialFormItem, currentItem);
  const isFinancingDirty = !isDeepEqual(initialFinancing, currentFinancing);
  const isFormDirty = entryMode === 'vehicle' ? isItemDirty || isFinancingDirty : isFinancingDirty;
  const hasErrors = Object.keys(errors).length > 0;

  const onFinancingConditionsSelected = useCallback(
    ({ vehicleAmount, registrationDate, paymentMethodOptions }: FinancingConditionsSelection) => {
      setLastConfirmedVehicleAmount(vehicleAmount);
      setLastConfirmedRegistrationDate(registrationDate);
      setSelectedPaymentMethodOptions(paymentMethodOptions);
    },
    [],
  );

  const clearSelectedPaymentMethodOptions = useCallback(() => {
    setSelectedPaymentMethodOptions(undefined);
  }, []);

  const resetSimulatorSelectionToInitial = useCallback(() => {
    setLastConfirmedVehicleAmount(initialFormItem.price);
    setLastConfirmedRegistrationDate(initialFormItem.registrationDate);
    setSelectedPaymentMethodOptions(undefined);
  }, [initialFormItem.price, initialFormItem.registrationDate]);

  return {
    lastConfirmedVehicleAmount,
    lastConfirmedRegistrationDate,
    selectedPaymentMethodOptions,
    needsFinancingReselection,
    requiresSimulatorFromVehicle,
    isItemDirty,
    isFinancingDirty,
    isFormDirty,
    hasErrors,
    onFinancingConditionsSelected,
    clearSelectedPaymentMethodOptions,
    resetSimulatorSelectionToInitial,
  };
};
